import React, { useState, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';
import { categoriesApi } from '../../services/api';
import { Category } from '../../types';

interface MultiCategorySelectProps {
  selectedCategories: string[];
  onChange: (categories: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  excludeCategories?: string[]; // Category names to exclude from the dropdown
}

export const MultiCategorySelect: React.FC<MultiCategorySelectProps> = ({
  selectedCategories,
  onChange,
  placeholder = 'Select categories...',
  disabled = false,
  className = '',
  excludeCategories = [],
}) => {
  // Debug: Log what the component receives
  console.log('🔍 MultiCategorySelect received selectedCategories:', selectedCategories);
  console.log('🔍 MultiCategorySelect selectedCategories type:', typeof selectedCategories);
  console.log('🔍 MultiCategorySelect selectedCategories isArray:', Array.isArray(selectedCategories));
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await categoriesApi.getAll();
      if (response.success && response.data) {
        const categoryData = Array.isArray(response.data) ? response.data : response.data;
        setCategories(categoryData);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedCategoryNames = () => {
    return categories
      .filter(cat => selectedCategories.includes(cat.id))
      .map(cat => cat.name);
  };

  const toggleCategory = (categoryId: string) => {
    if (selectedCategories.includes(categoryId)) {
      onChange(selectedCategories.filter(id => id !== categoryId));
    } else {
      onChange([...selectedCategories, categoryId]);
    }
  };

  const removeCategory = (categoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedCategories.filter(id => id !== categoryId));
  };

  const selectedNames = getSelectedCategoryNames();

  if (loading) {
    return (
      <div className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-50 ${className}`}>
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
          <span className="ml-2 text-gray-500">Loading categories...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div
        className={`w-full min-h-[2.75rem] p-3 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between ${
          disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'
        } ${isOpen ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-20' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex-1 flex flex-wrap gap-1">
          {selectedNames.length > 0 ? (
            selectedNames.map((name, index) => {
              const categoryId = categories.find(cat => cat.name === name)?.id;
              return (
                <span
                  key={categoryId || index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {name}
                  {!disabled && (
                    <button
                      type="button"
                      className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                      onClick={(e) => categoryId && removeCategory(categoryId, e)}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              );
            })
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        
        {!disabled && (
          <ChevronDown 
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        )}
      </div>

      {isOpen && !disabled && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {categories.length === 0 ? (
              <div className="p-3 text-gray-500 text-center">
                No categories available
              </div>
            ) : (
              [...categories]
                .filter(category => !excludeCategories.includes(category.name))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((category) => {
                  const isSelected = selectedCategories.includes(category.id);
                  return (
                    <div
                      key={category.id}
                      className={`p-3 cursor-pointer flex items-center justify-between hover:bg-gray-50 ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleCategory(category.id)}
                    >
                      <span className={`${isSelected ? 'text-blue-800 font-medium' : 'text-gray-700'}`}>
                        {category.name}
                      </span>
                      {isSelected && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                  );
                })
            )}
          </div>
        </>
      )}
    </div>
  );
};